import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { faceApi, FaceProfileDto } from '../../../api/faceApi';
import { employeeApi } from '../../../api/employeeApi';
import { RootState } from '../../../store/store';

interface FaceRegistrationEmployee {
  id: string;
  fullName: string;
  employeeCode: string;
}

interface CapturedFacePhoto {
  angleKey: string;
  label: string;
  imageBase64: string;
}

const CAPTURE_ANGLES = [
  { key: 'front', label: 'Front' },
  { key: 'left', label: 'Left Side' },
  { key: 'right', label: 'Right Side' },
  { key: 'up', label: 'Slightly Up' },
];

export const FaceRegistrationPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const canRegisterForOthers = user?.permissions?.includes('ManageEmployee') ?? false;

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [employees, setEmployees] = useState<FaceRegistrationEmployee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<FaceRegistrationEmployee | null>(null);
  const [employeeLoading, setEmployeeLoading] = useState(true);
  const [employeeError, setEmployeeError] = useState<string | null>(null);
  const [existingProfiles, setExistingProfiles] = useState<FaceProfileDto[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [capturedPhotos, setCapturedPhotos] = useState<CapturedFacePhoto[]>([]);
  const [activeAngleIndex, setActiveAngleIndex] = useState(0);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const employeeId = selectedEmployee?.id;
  const activeAngle = CAPTURE_ANGLES[activeAngleIndex] ?? CAPTURE_ANGLES[0];
  const minimumPhotosCaptured = capturedPhotos.length >= 3;

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (error) {
      setCameraError('Camera permission is required to register face photos.');
      toast.error('Failed to access camera. Please check browser camera permission.');
    }
  };

  const loadProfiles = async (id: string) => {
    setProfilesLoading(true);
    try {
      const profiles = await faceApi.getEmployeeFaceProfiles(id);
      setExistingProfiles(profiles);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to load registered face photos');
    } finally {
      setProfilesLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadEmployees = async () => {
      setEmployeeLoading(true);
      setEmployeeError(null);
      try {
        const current = await employeeApi.getCurrentEmployee();

        if (canRegisterForOthers) {
          const employeeList = await employeeApi.getAll(1, 500);
          if (cancelled) return;
          setEmployees(employeeList);
          setSelectedEmployee(
            current.type === 'Employee'
              ? employeeList.find((employee) => employee.id === current.id) ?? null
              : null
          );
        } else if (current.type === 'Employee') {
          if (cancelled) return;
          setEmployees([current]);
          setSelectedEmployee(current);
        } else {
          setEmployeeError('Your login is not linked to an employee profile.');
        }
      } catch (error: any) {
        if (!cancelled) {
          setEmployeeError(error?.response?.data?.message || 'Unable to load employee profile.');
        }
      } finally {
        if (!cancelled) setEmployeeLoading(false);
      }
    };

    void loadEmployees();
    return () => {
      cancelled = true;
    };
  }, [canRegisterForOthers]);

  useEffect(() => {
    if (!employeeId) {
      setExistingProfiles([]);
      setCapturedPhotos([]);
      return;
    }

    setCapturedPhotos([]);
    setActiveAngleIndex(0);
    void loadProfiles(employeeId);
  }, [employeeId]);

  useEffect(() => {
    void startCamera();
    return stopCamera;
  }, []);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !videoRef.current.videoWidth) {
      toast.warning('Camera is still loading. Please try again.');
      return;
    }

    const context = canvasRef.current.getContext('2d');
    if (!context) return;

    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    context.drawImage(videoRef.current, 0, 0);

    const imageBase64 = canvasRef.current.toDataURL('image/jpeg', 0.82);
    setCapturedPhotos((current) => {
      const next = current.filter((photo) => photo.angleKey !== activeAngle.key);
      return [...next, { angleKey: activeAngle.key, label: activeAngle.label, imageBase64 }];
    });

    const nextMissingIndex = CAPTURE_ANGLES.findIndex((angle, index) =>
      index > activeAngleIndex && !capturedPhotos.some((photo) => photo.angleKey === angle.key)
    );
    if (nextMissingIndex >= 0) {
      setActiveAngleIndex(nextMissingIndex);
    }
  };

  const removeCapturedPhoto = (angleKey: string) => {
    setCapturedPhotos((current) => current.filter((photo) => photo.angleKey !== angleKey));
  };

  const saveRegistration = async () => {
    if (!employeeId || !minimumPhotosCaptured) {
      toast.error('Capture at least 3 face photos before saving.');
      return;
    }

    setIsSaving(true);
    try {
      if (existingProfiles.length > 0) {
        await faceApi.deleteAllEmployeeFaceProfiles(employeeId);
      }

      for (const [index, photo] of capturedPhotos.entries()) {
        await faceApi.registerFace({
          employeeId,
          faceImageBase64: photo.imageBase64,
          profileName: photo.label,
          setAsPrimary: index === 0,
        });
      }

      toast.success('Face photos registered successfully');
      setCapturedPhotos([]);
      setActiveAngleIndex(0);
      await loadProfiles(employeeId);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to register face photos');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteProfile = async (profileId: string) => {
    if (!employeeId) return;
    setIsSaving(true);
    try {
      await faceApi.deleteFaceProfile(profileId);
      toast.success('Face photo deleted');
      await loadProfiles(employeeId);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to delete face photo');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteAllProfiles = async () => {
    if (!employeeId || existingProfiles.length === 0) return;
    const confirmed = window.confirm('Delete all registered face photos for this employee?');
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await faceApi.deleteAllEmployeeFaceProfiles(employeeId);
      setExistingProfiles([]);
      toast.success('All face photos deleted');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to delete face photos');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Face Registration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Capture 3 to 4 face photos for employee-wise face attendance.
              </Typography>
            </Box>

            {employeeError && <Alert severity="error">{employeeError}</Alert>}

            {canRegisterForOthers ? (
              <Autocomplete
                options={employees}
                value={selectedEmployee}
                loading={employeeLoading}
                onChange={(_, value) => setSelectedEmployee(value)}
                getOptionLabel={(option) => `${option.fullName} (${option.employeeCode})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    placeholder="Search employee"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {employeeLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : selectedEmployee ? (
              <Alert severity="info">
                Registering face for <strong>{selectedEmployee.fullName}</strong> ({selectedEmployee.employeeCode})
              </Alert>
            ) : null}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(320px, 0.8fr)' }, gap: 3 }}>
              <Box>
                <Paper sx={{ bgcolor: '#000', position: 'relative', overflow: 'hidden', minHeight: 420 }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: '100%', minHeight: 420, display: 'block', objectFit: 'cover' }}
                  />
                  <Box sx={{ position: 'absolute', left: 16, bottom: 16 }}>
                    <Chip color="primary" label={`Capture: ${activeAngle.label}`} />
                  </Box>
                </Paper>
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {cameraError && <Alert severity="error" sx={{ mt: 2 }}>{cameraError}</Alert>}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<CameraAltIcon />}
                    onClick={capturePhoto}
                    disabled={!selectedEmployee || isSaving}
                  >
                    Capture Photo
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ReplayIcon />}
                    onClick={startCamera}
                  >
                    Restart Camera
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={isSaving ? <CircularProgress color="inherit" size={18} /> : <SaveIcon />}
                    onClick={saveRegistration}
                    disabled={!selectedEmployee || !minimumPhotosCaptured || isSaving}
                  >
                    Save Registration
                  </Button>
                </Stack>
              </Box>

              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Capture Angles</Typography>
                  <Stack spacing={1}>
                    {CAPTURE_ANGLES.map((angle, index) => {
                      const photo = capturedPhotos.find((item) => item.angleKey === angle.key);
                      return (
                        <Paper
                          key={angle.key}
                          variant="outlined"
                          sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderColor: index === activeAngleIndex ? 'primary.main' : undefined }}
                        >
                          {photo ? (
                            <img src={photo.imageBase64} alt={photo.label} style={{ width: 58, height: 44, objectFit: 'cover', borderRadius: 4 }} />
                          ) : (
                            <Box sx={{ width: 58, height: 44, bgcolor: 'grey.100', borderRadius: 1 }} />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{angle.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {photo ? 'Captured' : 'Pending'}
                            </Typography>
                          </Box>
                          <Button size="small" onClick={() => setActiveAngleIndex(index)}>
                            Select
                          </Button>
                          {photo && (
                            <IconButton size="small" onClick={() => removeCapturedPhoto(angle.key)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                  {!minimumPhotosCaptured && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Minimum 3 photos are required.
                    </Alert>
                  )}
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6">Registered Photos</Typography>
                    <Button size="small" color="error" onClick={deleteAllProfiles} disabled={profilesLoading || isSaving || existingProfiles.length === 0}>
                      Delete All
                    </Button>
                  </Stack>

                  {profilesLoading ? (
                    <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
                  ) : existingProfiles.length === 0 ? (
                    <Alert severity="info">Face not registered for this employee.</Alert>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
                      {existingProfiles.map((profile) => (
                        <Paper key={profile.id} variant="outlined" sx={{ p: 1 }}>
                          <img
                            src={profile.faceImageBase64}
                            alt={profile.profileName}
                            style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 4 }}
                          />
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                            {profile.isPrimary && <CheckCircleIcon color="success" fontSize="small" />}
                            <Typography variant="caption" sx={{ flex: 1 }}>
                              {profile.profileName}
                            </Typography>
                            <IconButton size="small" onClick={() => deleteProfile(profile.id)} disabled={isSaving}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default FaceRegistrationPage;
